import React from 'react';

import { cn } from '@/lib/utils';
/**
 * Legacy missing field info structure (for backwards compatibility).
 * Used by components that haven't been updated to annotation_fields yet.
 */
interface GenericMissingFieldInfo {
  field_id: string;
  field_name: string;
  field_source: string;
  context_key?: string | null;
}

/**
 * New annotation field structure from the backend.
 * Contains all fields (not just missing ones) with their current values.
 */
export interface AnnotationField {
  field_name: string;
  current_value: string | null;
  value_source: 'context' | 'override' | null;
  is_missing: boolean;
}

interface MissingFieldsEditorProps {
  missingFields: Record<string, GenericMissingFieldInfo>;
  matterContext: Record<string, string | undefined>;
  overrides: Record<string, string>;
  setOverride: (key: string, value: string) => void;
}

/**
 * Legacy editor for missing fields (backwards compatibility).
 * @deprecated Use AnnotationFieldsEditor for new implementations.
 */
export const MissingFieldsEditor: React.FC<MissingFieldsEditorProps> = ({
  missingFields,
  matterContext,
  overrides,
  setOverride,
}) => {
  const fields = Object.values(missingFields);

  if (fields.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No missing fields - all values are populated.
      </p>
    );
  }

  const getCurrentValue = (field: GenericMissingFieldInfo): string => {
    const key = field.context_key;
    if (!key) return '';

    // 1. Check overrides first
    if (key in overrides) {
      return overrides[key];
    }

    // 2. Check matter context
    const contextValue = matterContext[key];
    if (contextValue !== undefined && contextValue !== null) {
      return String(contextValue);
    }

    return '';
  };

  const handleInputChange =
    (field: GenericMissingFieldInfo) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const key = field.context_key;
      if (key) {
        setOverride(key, e.target.value);
      }
    };

  return (
    <div className="space-y-2">
      {fields.map((field) => {
        const value = getCurrentValue(field);
        const isFromMatterContext = field.field_source === 'CONTEXT' || field.field_source === 'MATTER_CONTEXT';
        const isMissing = !value || value.trim() === '';

        return (
          <div key={field.field_id} className="flex flex-col">
            <label
              htmlFor={field.field_id}
              className={cn(
                'text-[11px] font-medium mb-1',
                isMissing ? 'text-red-700' : 'text-gray-700',
              )}
            >
              {field.field_name || field.context_key || 'Unknown field'}
              <span className="text-red-500 ml-1">*</span>
              {isFromMatterContext && (
                <span className="text-[9px] text-blue-600 ml-1.5">
                  (from matter)
                </span>
              )}
            </label>
            <input
              id={field.field_id}
              type="text"
              value={value}
              onChange={handleInputChange(field)}
              placeholder="Enter value"
              className={cn(
                'border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 w-full',
                isMissing
                  ? 'border-red-300 focus:ring-red-500 bg-red-50'
                  : 'border-gray-300 focus:ring-blue-500',
              )}
            />
            {isMissing && (
              <p className="text-[9px] text-red-600 mt-0.5">
                This field is required
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface AnnotationFieldsEditorProps {
  annotationFields: AnnotationField[];
  overrides: Record<string, string>;
  setOverride: (key: string, value: string) => void;
  /** If true, missing fields are optional (amber styling, no "required" message). Default: true */
  fieldsOptional?: boolean;
}

/**
 * Editor for annotation fields in merge document components.
 * Shows ALL fields (not just missing ones) with their current values.
 * Allows overriding any field, even those with existing values from context.
 * By default, fields are optional (not blocking) for merge documents.
 */
export const AnnotationFieldsEditor: React.FC<AnnotationFieldsEditorProps> = ({
  annotationFields,
  overrides,
  setOverride,
  fieldsOptional = true,
}) => {
  if (!annotationFields || annotationFields.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No annotation fields found in this template.
      </p>
    );
  }

  // Sort fields: missing first, then populated
  const sortedFields = [...annotationFields].sort((a, b) => {
    if (a.is_missing && !b.is_missing) return -1;
    if (!a.is_missing && b.is_missing) return 1;
    return 0;
  });

  const getCurrentValue = (field: AnnotationField): string => {
    // 1. Check overrides first (user-entered values take precedence)
    if (field.field_name in overrides) {
      return overrides[field.field_name];
    }

    // 2. Use current_value from backend (already resolved from context)
    if (field.current_value !== null) {
      return field.current_value;
    }

    return '';
  };

  const handleInputChange =
    (field: AnnotationField) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setOverride(field.field_name, e.target.value);
    };

  const missingCount = annotationFields.filter((f) => f.is_missing).length;
  const populatedCount = annotationFields.length - missingCount;

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="flex items-center gap-2 text-[10px]">
        {missingCount > 0 && (
          <span className="text-amber-600 font-medium">
            {missingCount} missing
          </span>
        )}
        {missingCount > 0 && populatedCount > 0 && (
          <span className="text-gray-300">|</span>
        )}
        {populatedCount > 0 && (
          <span className="text-green-600 font-medium">
            {populatedCount} populated
          </span>
        )}
      </div>

      {/* Fields */}
      <div className="space-y-2">
        {sortedFields.map((field) => {
          const value = getCurrentValue(field);
          const hasOverride = field.field_name in overrides;
          const isFromContext =
            field.value_source === 'context' && !hasOverride;
          const displayValue = value || '';
          const isMissing = field.is_missing && !displayValue;

          // Use amber for optional missing fields, red for required missing fields
          const missingColor = fieldsOptional ? 'amber' : 'red';

          return (
            <div key={field.field_name} className="flex flex-col">
              <label
                htmlFor={field.field_name}
                className={cn(
                  'text-[11px] font-medium mb-1 flex items-center gap-1',
                  isMissing
                    ? fieldsOptional
                      ? 'text-amber-700'
                      : 'text-red-700'
                    : 'text-gray-700',
                )}
              >
                <span>{field.field_name}</span>
                {/* Only show required asterisk if fields are not optional */}
                {field.is_missing && !fieldsOptional && (
                  <span className="text-red-500">*</span>
                )}
                {isMissing && fieldsOptional && (
                  <span className="text-[9px] text-amber-600 font-normal">
                    (optional)
                  </span>
                )}
                {isFromContext && (
                  <span className="text-[9px] text-green-600 font-normal">
                    (from matter)
                  </span>
                )}
                {hasOverride && (
                  <span className="text-[9px] text-indigo-600 font-normal">
                    (overridden)
                  </span>
                )}
              </label>
              <input
                id={field.field_name}
                type="text"
                value={displayValue}
                onChange={handleInputChange(field)}
                placeholder={
                  field.is_missing
                    ? fieldsOptional
                      ? 'Enter value (optional)'
                      : 'Enter value'
                    : 'Override value'
                }
                className={cn(
                  'border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 w-full transition-colors',
                  isMissing
                    ? fieldsOptional
                      ? 'border-amber-200 focus:ring-amber-500 bg-amber-50/50'
                      : 'border-red-300 focus:ring-red-500 bg-red-50'
                    : isFromContext
                      ? 'border-green-200 focus:ring-green-500 bg-green-50/50'
                      : hasOverride
                        ? 'border-indigo-200 focus:ring-indigo-500 bg-indigo-50/50'
                        : 'border-gray-300 focus:ring-blue-500',
                )}
              />
              {/* Only show required message if fields are not optional */}
              {isMissing && !fieldsOptional && (
                <p className="text-[9px] text-red-600 mt-0.5">
                  This field is required
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
